import { useState } from 'react';
import {
    CWTSchemaField,
    CWTSchemaSection,
    CWTSchemaTable,
    FieldType as FieldTypeEnum,
    ListType,
    SectionType,
    TableType,
} from '../../../../types/custom-worksheet-v2';
import {
    buildPropertyPath,
    getPinnedField,
    isCWTSchemaField,
    isCWTSchemaSection,
    isCWTSchemaTable,
    isCWTSchemaTableRow,
} from '../../store/utils';
import { getReference } from '../../../../components/custom-worksheet/utils';
import { editListSummary, editPropertyDetails } from '../../store/slice';
import { ChevronDownIcon } from '@heroicons/react/20/solid';
import { AnimatePresence, motion } from 'framer-motion';
import { useDispatch, useSelector } from 'react-redux';
import { selectSchema } from '../../store/selectors';
import { NUMERIC_TYPES } from '../../../../constants';
import PropertyReference from '../components/PropertyReference';
import FieldType from '../components/field/FieldType';
import FieldDefaultValue from '../components/field/FieldDefaultValue';
import ToggleField from './ToggleField';
import AdditionalFieldTypes from './AdditionalFieldTypeRows';
import MinimumItems from './MinimumItems';
import TableOrSectionSummary from './TableOrSectionSummary';

type ConfigurationsProps = {
    property: CWTSchemaField | CWTSchemaTable | CWTSchemaSection;
};

function Configurations({ property }: ConfigurationsProps): JSX.Element {
    const [isCollapsed, setIsCollapsed] = useState(false);

    const dispatch = useDispatch();
    const schema = useSelector(selectSchema);

    const propertyPaths = buildPropertyPath(schema, property);
    const listParentProperty = propertyPaths.find((p) => p.type === ListType.LIST);
    const hasTableOrSectionParentProperty = propertyPaths
        .slice(0, -1)
        .find((p) => p.type === TableType.TABLE || p.type === SectionType.SECTION);

    /**
     * Only show toggle for list summary if
     *  - Property belongs in a list
     *  - Property is not a table (TEMPORARY DISABLE UNTIL THIS FEATURE IS IN)
     *  - Property does not belong in a table
     *  - Property does not belong in a section
     */
    const showListSummary = listParentProperty && property.type !== TableType.TABLE && !hasTableOrSectionParentProperty;

    /**
     * If property belongs in a list, check if property can be included in list summary:
     *
     * Property must be a SECTION with pinned field and type of pinned field is one of the NUMERIC_TYPES
     *   OR
     * Property must be a FIELD that does not belong in a table/section and type is one of the NUMERIC_TYPES
     */
    let allowListSummary = false;
    if (listParentProperty) {
        if (isCWTSchemaSection(property)) {
            const sectionPinnedField = getPinnedField(property);
            allowListSummary = sectionPinnedField !== null && NUMERIC_TYPES.includes(sectionPinnedField.type);
        } else if (isCWTSchemaField(property)) {
            const typeIsAllowed = NUMERIC_TYPES.includes(property.type);
            const isNotInSectionOrTable =
                propertyPaths.find((p) => p.type === SectionType.SECTION || p.type === TableType.TABLE) === undefined;

            allowListSummary = isNotInSectionOrTable && typeIsAllowed;
        }
    }

    function handleToggleAdditionalFields(): void {
        if (!isCWTSchemaSection(property)) {
            return;
        }

        dispatch(
            editPropertyDetails({
                id: `${property.id}.additionalFields`,
                value: property.additionalFields ? null : Object.values(FieldTypeEnum).filter((v) => v !== FieldTypeEnum.DROPDOWN),
            })
        );
    }

    return (
        <div className="flex flex-col">
            <div className="flex items-center pl-4 pt-4">
                <ChevronDownIcon
                    className={`hover:cursor-pointer w-4 h-4 -ml-4 text-gray-700 transition-transform ${isCollapsed ? '-rotate-90' : ''}`}
                    onClick={(): void => setIsCollapsed(!isCollapsed)}
                />
                <p className="text-sm font-bold leading-none text-gray-700">Configurations</p>
            </div>

            <AnimatePresence initial={false}>
                {!isCollapsed && (
                    <motion.div
                        exit={{ height: 0, opacity: 0 }}
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        transition={{ height: { duration: 0.2 } }}
                    >
                        <div className="mt-4 flex flex-col gap-4 pl-4 pb-4 pr-0.5">
                            {/* TYPE */}
                            {isCWTSchemaField(property) && (
                                <div className="flex flex-col gap-1">
                                    <Description
                                        title="Type"
                                        description="Restrict what values can inputted into the field. Depending on the selected type, the input may also alter its
                        appearance and functionality."
                                    />
                                    <FieldType id={property.id} fieldType={property.type} showActionButtons={false} />
                                </div>
                            )}

                            {/* DESCRIPTION */}
                            <div className="flex flex-col gap-1">
                                <Description
                                    title="Reference"
                                    description="This value is what's used to reference this field in calculations and functions."
                                />
                                <PropertyReference id={property.id} showReadLayout={false} />
                            </div>

                            {/* DEFAULT VALUE */}
                            {isCWTSchemaField(property) && <FieldDefaultValue field={property} />}

                            {/* SHOW SUMMARY ROW FOR TABLE/SECTION */}
                            {(isCWTSchemaTable(property) || isCWTSchemaSection(property)) && <TableOrSectionSummary property={property} />}

                            {/* SHOW OPTION TO INCLUDE FIELD/SECTION SUMMARY IN LIST */}
                            {showListSummary && (
                                <ToggleField
                                    title="Include in List Summary"
                                    description={`Add a ${
                                        isCWTSchemaField(property) ? 'field' : 'section summary'
                                    } value to the list summary.`}
                                    defaultEnabled={Boolean(listParentProperty.summary?.properties[getReference(property.id)])}
                                    disabledHint={
                                        !allowListSummary
                                            ? isCWTSchemaField(property)
                                                ? 'Field must be one of these types - Accounting / Money / Number / Percent'
                                                : 'Section must have a summary or pinned field value of these types - Accounting / Money / Number / Percent'
                                            : undefined
                                    }
                                    onToggle={(): void => {
                                        dispatch(editListSummary({ id: listParentProperty.id, summarisedPropertyId: property.id }));
                                    }}
                                />
                            )}

                            {/* ALLOW ADDITIONAL FIELDS FOR SECTION */}
                            {isCWTSchemaSection(property) && (
                                <div className="flex flex-col gap-1">
                                    <ToggleField
                                        title="Additional Fields"
                                        description="Allow additional fields to be added when completing the worksheet."
                                        defaultEnabled={property.additionalFields !== null}
                                        onToggle={handleToggleAdditionalFields}
                                    />
                                    {property.additionalFields && (
                                        <AdditionalFieldTypes sectionId={property.id} types={property.additionalFields} />
                                    )}
                                </div>
                            )}

                            {isCWTSchemaTable(property) && (
                                <MinimumItems
                                    isDisabled={Boolean(property.generatedFromFunction)}
                                    id={property.id}
                                    defaultValue={property.config?.minRows ?? 0}
                                />
                            )}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}

export default Configurations;

type DescriptionProps = {
    title: string;
    description: string;
    isOptional?: boolean;
};

export function Description({ title, description, isOptional = false }: DescriptionProps): JSX.Element {
    return (
        <div className="flex flex-col gap-1 grow">
            <div className="flex text-xs font-medium ">
                <p className="text-gray-700 grow">{title}</p>
                {isOptional && <p className="text-gray-500">Optional</p>}
            </div>
            <p className="text-xs text-gray-600">{description}</p>
        </div>
    );
}
